import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import {
  DefaultButton,
  MessageBar,
  MessageBarType,
  Pivot,
  PivotItem,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
  TextField,
} from "@fluentui/react";
import { AdminStore } from "./admin/AdminStore";
import { TrialRequestDetail } from "./admin/TrialRequestDetail";
import { TrialRequestList } from "./admin/TrialRequestList";
import { LeadList } from "./admin/LeadList";
import { LeadDetail } from "./admin/LeadDetail";
import { ActionList } from "./admin/ActionList";
import { ActionDetail } from "./admin/ActionDetail";
import { AdminPagination } from "./admin/AdminPagination";

export const AdminApp = observer(() => {
  const store = useMemo(() => new AdminStore(), []);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (store.isAuthenticated) {
      store.loadActiveTab().finally(() => setInitialized(true));
    } else {
      setInitialized(true);
    }
  }, [store]);

  const handleVerifyPin = async () => {
    const ok = await store.verifyPin();
    if (ok) {
      await store.loadActiveTab();
    }
  };

  const isDetailView =
    store.selectedRequest !== null ||
    store.selectedLead !== null ||
    store.selectedAction !== null;

  const renderEmptyMessage = (label: string) => (
    <Text>No {label} yet.</Text>
  );

  const renderTrialRequestsTab = () => {
    if (store.selectedRequest) {
      return (
        <TrialRequestDetail
          request={store.selectedRequest}
          onBack={() => { store.selectedRequest = null; }}
        />
      );
    }
    return (
      <Stack styles={{ root: { padding: 24 } }}>
        {store.trialRequests.items.length === 0 && !store.loading
          ? renderEmptyMessage("trial requests")
          : (
            <>
              <TrialRequestList
                requests={store.trialRequests.items}
                onSelect={(request) => store.loadRequestDetail(request.id)}
              />
              <AdminPagination
                page={store.trialRequests.page}
                pageSize={store.trialRequests.pageSize}
                total={store.trialRequests.total}
                loading={store.loading}
                onPageChange={(page) => store.loadTrialRequests(page)}
              />
            </>
          )}
      </Stack>
    );
  };

  const renderLeadsTab = () => {
    if (store.selectedLead) {
      return (
        <LeadDetail
          lead={store.selectedLead}
          onBack={() => { store.selectedLead = null; }}
        />
      );
    }
    return (
      <Stack styles={{ root: { padding: 24 } }}>
        {store.leads.items.length === 0 && !store.loading
          ? renderEmptyMessage("leads")
          : (
            <>
              <LeadList
                leads={store.leads.items}
                onSelect={(lead) => store.loadLeadDetail(lead.email)}
              />
              <AdminPagination
                page={store.leads.page}
                pageSize={store.leads.pageSize}
                total={store.leads.total}
                loading={store.loading}
                onPageChange={(page) => store.loadLeads(page)}
              />
            </>
          )}
      </Stack>
    );
  };

  const renderActionsTab = () => {
    if (store.selectedAction) {
      return (
        <ActionDetail
          action={store.selectedAction}
          onBack={() => { store.selectedAction = null; }}
        />
      );
    }
    return (
      <Stack styles={{ root: { padding: 24 } }}>
        {store.actions.items.length === 0 && !store.loading
          ? renderEmptyMessage("actions")
          : (
            <>
              <ActionList
                actions={store.actions.items}
                onSelect={(action) => store.selectAction(action)}
              />
              <AdminPagination
                page={store.actions.page}
                pageSize={store.actions.pageSize}
                total={store.actions.total}
                loading={store.loading}
                onPageChange={(page) => store.loadActions(page)}
              />
            </>
          )}
      </Stack>
    );
  };

  if (!initialized) {
    return (
      <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: "60vh" } }}>
        <Spinner size={SpinnerSize.large} label="Loading..." />
      </Stack>
    );
  }

  if (!store.isAuthenticated) {
    return (
      <Stack
        tokens={{ childrenGap: 16 }}
        styles={{ root: { maxWidth: 420, margin: "48px auto", padding: 24 } }}
      >
        <Text variant="xxLarge">Innofalls Admin</Text>
        <Text>Sign in with a one-time PIN sent to the admin email.</Text>
        {store.error && (
          <MessageBar messageBarType={MessageBarType.error}>{store.error}</MessageBar>
        )}
        {!store.pinRequested ? (
          <PrimaryButton
            text="Request PIN"
            disabled={store.loading}
            onClick={() => store.requestPin()}
          />
        ) : (
          <>
            <MessageBar messageBarType={MessageBarType.info}>
              A PIN was sent to the admin email. Enter it below.
            </MessageBar>
            <TextField
              label="PIN"
              value={store.pin}
              maxLength={6}
              onChange={(_, value) => { store.pin = value ?? ""; }}
            />
            <PrimaryButton
              text="Verify PIN"
              disabled={store.loading || store.pin.length !== 6}
              onClick={handleVerifyPin}
            />
            <DefaultButton
              text="Request new PIN"
              disabled={store.loading}
              onClick={() => store.requestPin()}
            />
          </>
        )}
        {store.loading && <Spinner size={SpinnerSize.medium} label="Working..." />}
      </Stack>
    );
  }

  return (
    <Stack styles={{ root: { minHeight: "100vh", background: "#fff" } }}>
      <Stack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        styles={{ root: { padding: "16px 24px", borderBottom: "1px solid #eee" } }}
      >
        <Text variant="xLarge">Innofalls Admin</Text>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <DefaultButton
            text="Refresh"
            disabled={store.loading}
            onClick={() => store.refreshActiveTab()}
          />
          <DefaultButton text="Logout" onClick={() => store.logout()} />
        </Stack>
      </Stack>

      {store.error && (
        <MessageBar messageBarType={MessageBarType.error} styles={{ root: { margin: 16 } }}>
          {store.error}
        </MessageBar>
      )}

      {store.loading && !isDetailView && (
        <Spinner size={SpinnerSize.medium} label="Loading..." styles={{ root: { margin: 24 } }} />
      )}

      {!isDetailView ? (
        <>
          <Pivot
            selectedKey={store.activeTab}
            onLinkClick={(item) => {
              if (item?.props.itemKey) {
                store.setActiveTab(item.props.itemKey as "trial-requests" | "leads" | "actions");
              }
            }}
            styles={{ root: { padding: "0 24px" } }}
          >
            <PivotItem headerText="Trial Requests" itemKey="trial-requests" />
            <PivotItem headerText="Leads" itemKey="leads" />
            <PivotItem headerText="Actions" itemKey="actions" />
          </Pivot>
          {store.activeTab === "trial-requests" && renderTrialRequestsTab()}
          {store.activeTab === "leads" && renderLeadsTab()}
          {store.activeTab === "actions" && renderActionsTab()}
        </>
      ) : (
        <>
          {store.selectedRequest && renderTrialRequestsTab()}
          {store.selectedLead && renderLeadsTab()}
          {store.selectedAction && renderActionsTab()}
        </>
      )}
    </Stack>
  );
});
